/// <amd-module name="Wit/HtmlEditor/Components/HtmlEditorInputProxy" />
import { Editor, InsertOption, ContentPosition } from "roosterjs-react";
import { IAutocompleteInputProxy, IAutocompleteInputSelection } from "Mention/Autocomplete/AutocompleteInputProxy";

export function createHTMLEditorInputProxy(contentElement: HTMLDivElement, getEditor: () => Editor | undefined): IAutocompleteInputProxy {
    return new HtmlEditorInputProxy(contentElement, getEditor);
}

class HtmlEditorInputProxy implements IAutocompleteInputProxy {
    private _listeners: { eventName: string; listener: (e: any) => void }[] = [];
    public constructor(private _contentElement: HTMLDivElement, private _getEditor: () => Editor | undefined) {}

    /* Delegate these methods directly to the content element */
    getElementForAnchor = () => this._contentElement;
    getClientWidth = () => this._contentElement.clientWidth;
    setInputAttribute = (qualifiedName: string, value: string) => this._contentElement.setAttribute(qualifiedName, value);
    getInputAttribute = (qualifiedName: string) => this._contentElement.getAttribute(qualifiedName);
    removeInputAttribute = (qualifiedName: string) => this._contentElement.removeAttribute(qualifiedName);

    /* Delegate these methods directly to the editor component */
    focus = () => this._getEditor()?.focus();

    public addKeyDownListener(onKeyDown: (e: KeyboardEvent) => void) {
        this._listeners.push({ eventName: "keydown", listener: onKeyDown });
        this._contentElement.addEventListener("keydown", onKeyDown);
    }

    public addKeyUpListener(onKeyUp: (e: KeyboardEvent) => void) {
        this._listeners.push({ eventName: "keyup", listener: onKeyUp });
        this._contentElement.addEventListener("keyup", onKeyUp);
    }

    public addInputListener(onInput: () => void) {
        this._listeners.push({ eventName: "input", listener: onInput });
        this._contentElement.addEventListener("input", onInput);
    }

    public addBlurListener(onBlur: (e: FocusEvent) => void) {
        this._listeners.push({ eventName: "blur", listener: onBlur });
        this._contentElement.addEventListener("blur", onBlur);
    }

    public addPasteListener(onPaste: (e: ClipboardEvent) => void) {
        this._listeners.push({ eventName: "paste", listener: onPaste });
        this._contentElement.addEventListener("paste", onPaste);
    }

    public removeListeners() {
        for (const { eventName, listener } of this._listeners) {
            this._contentElement.removeEventListener(eventName, listener);
        }
    }

    isTargetedByEvent(ev?: Event | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): boolean {
        return !!ev && ev.target === this._contentElement;
    }

    public getSelection(): IAutocompleteInputSelection {
        const selection = this._getEditor()?.getSelection();
        if (!selection) {
            // Treat the entire editor content as the selection
            return { selectionStart: 0, selectionEnd: 0, value: this._getEditor()?.getContent() ?? "" };
        }

        return { selectionStart: selection.anchorOffset, selectionEnd: selection.anchorOffset, value: selection.focusNode?.textContent ?? "" };
    }

    public insertCalloutAnchor(anchor: HTMLElement): void {
        // TODO - for now anchoring to the bottom of the input element is good enough, but we could improve that by implementing this.
    }

    public insertText(result: string, startIndex: number): void {
        const editor = this._getEditor();
        if (!editor) {
            return;
        }

        editor.addUndoSnapshot((start, end, snapshot) => {
            const selectionStart = { ...start, offset: startIndex };
            editor.select(selectionStart, start);
            editor.insertContent(result, {
                replaceSelection: true,
                updateCursor: true,
                position: ContentPosition.SelectionStart
            } as InsertOption);
        });
    }
}
